import React, { useState } from 'react';
import classnames from '../utils/classnames.tsx';
import { withIcon } from './Icon.tsx';
import { faClipboard, faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import StringField, { StringFieldProps } from './Fields/StringField.tsx';
import Field from './Fields/Field.tsx';
import { useTranslation } from 'react-i18next';
import { useToaster } from './Toast.tsx';
import Tooltip from './Tooltip.tsx';

const ShowIcon = withIcon(faEye);
const HideIcon = withIcon(faEyeSlash);
const CopyIcon = withIcon(faClipboard);

const Secret = ({
    value,
    className,
    onClick,
    fallbackLength = 8,
    helpText,
    ...props
}: React.ComponentPropsWithRef<'span'> & {
    value?: string;
    fallbackLength?: number;
    helpText?: string;
}) => {
    const { publishToast } = useToaster();
    const { t } = useTranslation('core');

    const handleCopy = (e) => {
        if (!value) return;

        window.getSelection().selectAllChildren(e.currentTarget);

        navigator.clipboard
            .writeText(value)
            .then(() => publishToast({ description: t('components.secret.copySuccess'), status: 'success' }))
            .catch(() => publishToast({ description: t('components.secret.copyFailed'), status: 'error' }));
    };

    // same length to prevent layout shift when toggling visibility
    const placeholder = '•'.repeat(value?.length || fallbackLength);

    return (
        <Tooltip content={value && t(`components.secret.copyTooltip`)}>
            <span className="inline-flex flex-col gap-1 items-start">
                <span
                    className={classnames(
                        'font-mono rounded transition-colors py-0.5 inline-flex items-center gap-1 group text-content-strong',
                        value && 'cursor-pointer hover:bg-surface-strong pl-1',
                        !value && 'select-none',
                        className
                    )}
                    onClick={handleCopy}
                    {...props}
                >
                    <span>{value ? value : placeholder}</span>
                    {value && (
                        <span className="text-content-medium group-hover:text-content-strong transition-colors w-5 text-center select-none">
                            <CopyIcon />
                        </span>
                    )}
                </span>
                {helpText && <span className="text-content-medium text-xs">{helpText}</span>}
            </span>
        </Tooltip>
    );
};

interface SecretFieldProps extends Omit<StringFieldProps, 'controls'> {
    secretName: string;
    secretId?: string;
    defaultIsVisible?: boolean;
}

export const SecretField = ({ secretName, secretId, defaultIsVisible = false, ...props }: SecretFieldProps) => {
    const [isVisible, setIsVisible] = useState(defaultIsVisible);

    const handleToggleVisibility = () => {
        setIsVisible(!isVisible);
    };

    const EyeButton = () => (
        <Field.ControlButton onClick={handleToggleVisibility}>
            {isVisible ? <HideIcon /> : <ShowIcon />}
        </Field.ControlButton>
    );

    return <StringField type={isVisible ? 'text' : 'password'} controls={<EyeButton />} {...props} />;
};

export default Secret;
